import LightCustomButton from "@components/buttons/LightCustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import { invoiceServices } from "@services/apis/Invoice";
import { AdminNote } from "@services/models/InvoiceApprovals/GetInvoiceApprovalsModel";
import React, { useState } from "react";
import UploadFileComponent from "src/modules/InvoiceApprovals/components/UploadFileComponent";

interface EnhancedNotesSectionProps {
  notes: AdminNote[];
  onNotesChange: (notes: AdminNote[]) => void;
  requestId: number;
}

const EnhancedNotesSection: React.FC<EnhancedNotesSectionProps> = ({
  notes,
  onNotesChange,
  requestId,
}) => {
  const [newNoteContent, setNewNoteContent] = useState<string>("");
  const [newNoteAttachments, setNewNoteAttachments] = useState<
    {
      name: string;
      type: string;
      url?: string;
    }[]
  >([]);
  const [isAddingNote, setIsAddingNote] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;

    const newNote: AdminNote = {
      content: newNoteContent,
      timestamp: new Date().toISOString(),
      attachments: newNoteAttachments
        .filter((file) => typeof file.url === "string" && file.url.length > 0)
        .map((file) => ({
          url: file.url as string,
          name: file.name,
          type: file.type,
        })),
    };

    const updatedNotes = [...notes, newNote];
    onNotesChange(updatedNotes);

    try {
      setIsSaving(true);
      await invoiceServices.updateInvoiceTransactionData({
        requestId,
        transactionData: { adminNotes: updatedNotes },
      });
      setNewNoteContent("");
      setNewNoteAttachments([]);
      setIsAddingNote(false);
    } catch (e) {
      onNotesChange(notes);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAttachmentUpload = (
    file: { name: string; type: string; url?: string } | null
  ) => {
    if (!file) return;
    setNewNoteAttachments((prev) => [...prev, file]);
  };

  return (
    <div className="flex flex-col border border-border-subtle bg-bg-base rounded-lg">
      <div
        className="flex flex-row justify-between items-center py-[12px] px-[12px] cursor-pointer"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <p className="text-text-base text-[10px] font-semibold">Admin Notes</p>
        <div className="flex items-center gap-2">
          <span className="text-text-subtle text-[10px]">
            {notes.length} {notes.length === 1 ? "note" : "notes"}
          </span>
          <span
            className="material-symbols-outlined text-[18px] text-text-base transform transition-transform duration-200"
            style={{
              transform: !isCollapsed ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            expand_more
          </span>
        </div>
      </div>

      {!isCollapsed && (
        <div className="px-[12px] pb-[12px] gap-3 flex flex-col">
          <div className="border-b border-border-subtle"></div>
          <div className="border border-border-subtle rounded-[8px] flex flex-col overflow-hidden">
            <div className="rounded-t-[8px] bg-bg-page-body pl-[12px] pr-[24px] py-[8px] flex flex-row border-b border-b-border-subtle">
              <span
                className="font-semibold text-[10px] text-text-base"
                style={{ width: "80%" }}
              >
                Notes
              </span>
              <span
                className="font-semibold text-[10px] text-text-base"
                style={{ width: "20%" }}
              >
                Attachments
              </span>
            </div>
            {notes.length === 0 ? (
              <div className="px-[12px] py-[12px] text-text-subtle text-[12px]">
                No notes added yet
              </div>
            ) : (
              <div className="flex flex-col">
                {notes.map((note, idx) => (
                  <div
                    key={idx}
                    className="px-[12px] py-[12px] flex bg-bg-page-body flex-row border-b border-b-border-subtle"
                  >
                    <span
                      className="flex text-[10px] text-text-base items-start"
                      style={{ width: "80%" }}
                    >
                      {note.content}
                    </span>
                    <div
                      className="flex items-center justify-end"
                      style={{ width: "20%" }}
                    >
                      {(note.attachments || []).map((attachment, aIdx) => (
                        <div key={aIdx} className="flex items-center gap-1">
                          <button
                            onClick={() =>
                              attachment.url &&
                              window.open(attachment.url, "_blank")
                            }
                            className="flex items-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                          >
                            <span className="material-symbols-outlined text-[14px]">
                              open_in_new
                            </span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {isAddingNote ? (
            <div className="flex flex-col gap-3 p-3 bg-bg-page-body rounded-lg">
              <GlobalTextInputField
                title="Add Note"
                placeholder="Enter your note..."
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                fontSize="12px"
              />
              <UploadFileComponent
                text="Attachments"
                onFileUpload={handleAttachmentUpload}
                fileId="admin_notes_attachment"
              />
              <div className="flex gap-2 w-full">
                <div className="flex-1">
                  <LightCustomButton
                    text="SAVE"
                    onClick={handleAddNote}
                    isActive={newNoteContent.trim().length > 0}
                    textSize="10px"
                    isButtonLoading={isSaving}
                    className="w-full"
                  />
                </div>
                <div className="flex-1">
                  <LightCustomButton
                    text="CANCEL"
                    onClick={() => {
                      setIsAddingNote(false);
                      setNewNoteContent("");
                      setNewNoteAttachments([]);
                    }}
                    isActive={true}
                    textSize="10px"
                    borderColor="border-text-status-red"
                    buttonColor="bg-bg-page-body"
                    className="w-full text-text-status-red"
                  />
                </div>
              </div>
            </div>
          ) : (
            <LightCustomButton
              text="ADD NOTE"
              onClick={() => setIsAddingNote(true)}
              isActive={!isSaving}
              textSize="12px"
              borderColor="border-border-button-secondary"
              buttonColor="bg-bg-page-body"
              className="w-full text-text-button-tertiary"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default EnhancedNotesSection;
